import {
  getShapeById as baseGetShapeById,
  createInputSchema,
  dereferenceObjectSchema,
  type FormConfig,
  type FormMap,
  type FormsConfig,
  followRef,
  getServiceInfo,
  isBuiltinShape,
  isBuiltinType,
  isObjectSchema,
  listCachedShapes,
  type ProjectSchemaJSON,
  type PropertySchema,
  type QueryJSON,
  SERVICE_OBJECT_PATTERN_NAME,
  type ServiceConfigJSON,
  type ShapeJSON,
  shapeToPropertySchema
} from '@takeshape/schema';
import { assert, formatShapeName, getSingle } from '@takeshape/util';
import { getRuntimeSchema, getUserSchema } from 'app/branches/selectors';
import { produce } from 'immer';
import { fromJS } from 'immutable';
import get from 'lodash/get';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import { getRouter } from '../selectors.js';
import { SCHEMA_NAME, type SchemaSlice, type SchemaState } from './slice.js';
import { SCHEMA_HISTORY_NAME, type SchemaHistory, type SchemaHistorySlice } from './slices/get-schema-version-list.js';
import type { SchemaFilters } from './types.js';
import { createFallbackForm } from './util/forms.js';

export const getState = (state: SchemaSlice): SchemaState => state[SCHEMA_NAME];
export const getSchema = createSelector(getRuntimeSchema, (schema) => {
  if (!schema) {
    throw new Error('Schema has not been loaded!');
  }

  return schema;
});

export const getPropertySchema = createSelector(
  [getSchema, (_: SchemaSlice, schemaPath: string[]) => schemaPath],
  (projectSchema, schemaPath) => get(projectSchema, schemaPath)
);

export const getSchemaVersion = (state: SchemaSlice): number => getSchema(state).version;
const getShapeName = (_state: unknown, props: { shapeName: string }): string => props.shapeName;
const getShapeId = (_state: unknown, props: { shapeId: string }): string => props.shapeId;
const getPropsLocale = (_state: unknown, props: { locale?: string }): string | undefined => props.locale;
const getServiceId = (_state: unknown, props: { serviceId?: string }): string | undefined => props.serviceId;

export const getProjectId = createSelector(getSchema, (schema) => schema.projectId);
export const getApiVersion = createSelector(getSchema, (schema) => schema.apiVersion);
export const getVersion = createSelector(getSchema, (schema) => schema.version);

// Locales
export const getDefaultLocale = createSelector(getSchema, (schema) => schema.defaultLocale);
export const getLocales = createSelector(getSchema, (schema) => schema.locales);
export const getLocale = createSelector(
  getDefaultLocale,
  getPropsLocale,
  (defaultLocale, propsLocale) => propsLocale ?? defaultLocale
);

// Shapes
export const getShapeIdByName = createSelector(
  getSchema,
  getShapeName,
  (projectSchema, shapeName): string | undefined =>
    (projectSchema.shapes[shapeName] || projectSchema.shapes[formatShapeName(shapeName)])?.id
);

export const getInputSchema = createSelector(getSchema, (projectSchema): ProjectSchemaJSON => {
  return createInputSchema(projectSchema);
});

/**
 * Get a Shape with `@input` rewrites made.
 */
export const getInputShapeByName = createSelector(
  getInputSchema,
  getShapeName,
  (projectSchema, shapeName): ShapeJSON | undefined => {
    return projectSchema.shapes[shapeName] || projectSchema.shapes[formatShapeName(shapeName)];
  }
);

export const getShapeByName = createSelector(
  getSchema,
  getShapeName,
  (projectSchema, shapeName): ShapeJSON | undefined =>
    projectSchema.shapes[shapeName] || projectSchema.shapes[formatShapeName(shapeName)]
);

// Queries & Mutations
const getName = (_state: unknown, props: { name: string }): string => props.name;

export const getQueryByName = createSelector(
  getSchema,
  getName,
  (projectSchema, name): QueryJSON | undefined => projectSchema.queries[name]
);

export const getMutationByName = createSelector(
  getSchema,
  getName,
  (projectSchema, name): QueryJSON | undefined => projectSchema.mutations[name]
);

export const hasModelShapes = createSelector(getUserSchema, (schema) => {
  return Object.values(schema?.shapes ?? {}).some((shape) => shape.model !== undefined);
});

export const getFormForFormlessShape = createSelector(getSchema, getShapeName, createFallbackForm);

export const getFormForFormlessShapeImmutable = createSelector(getFormForFormlessShape, (schemaAndForm) => {
  return {
    contentSchema: schemaAndForm.contentSchema,
    formConfig: fromJS(schemaAndForm.formConfig)
  };
});

export const getFormByName = createSelector(
  getSchema,
  getShapeName,
  (projectSchema, shapeName): FormsConfig | undefined => {
    const form = projectSchema.forms?.[shapeName] ?? projectSchema.forms?.[formatShapeName(shapeName)];
    return (
      form ?? {
        default: createFallbackForm(projectSchema, shapeName).formConfig
      }
    );
  }
);

export const getShapeById = createSelector(getSchema, getShapeId, (projectSchema, shapeId): ShapeJSON | undefined =>
  baseGetShapeById(projectSchema, shapeId)
);

export const getFormById = createSelector(
  getSchema,
  getShapeById,
  (projectSchema, shape): FormsConfig | undefined => shape && projectSchema.forms?.[shape.name]
);

export const getForms = createSelector(getSchema, (projectSchema): FormMap | undefined => projectSchema.forms);

const hidden = new Set(['ASSET', 'SITE']);
export const getShapeList = createSelector(getSchema, (projectSchema): ShapeJSON[] =>
  Object.values(projectSchema.shapes).filter((shape) => Boolean(shape.model) && !hidden.has(shape.id))
);

export type IdOption = {
  label: string;
  value: string;
};

export type PropertyOption = {
  label: string;
  value: { '@ref': string };
};

export type Option = IdOption | PropertyOption;

export type ShapeOptionsProps = {
  format: 'ids' | 'refs';
  filter: 'models' | 'non-models';
};

export function toOption(shape: ShapeJSON, format?: ShapeOptionsProps['format']): Option {
  return format === 'ids' ? toIdOption(shape) : toPropertyOption(shape);
}

function toIdOption(shape: ShapeJSON): IdOption {
  return {
    label: shape.title,
    value: shape.id
  };
}

function toPropertyOption(shape: ShapeJSON): PropertyOption {
  return {
    label: shape.title,
    value: { '@ref': `local:${shape.name}` }
  };
}

const getShapeOptionsHelper = <T>(
  projectSchema: ProjectSchemaJSON,
  filter: 'models' | 'non-models' | 'any',
  toOption: (shape: ShapeJSON) => T,
  excludeFormlessShapes = true
): T[] => {
  return Object.values(projectSchema.shapes)
    .filter(
      (shape) =>
        (filter === 'any' ? true : filter === 'models' ? shape.model : !shape.model) &&
        (!excludeFormlessShapes || projectSchema.forms?.[shape.name]) &&
        !isBuiltinShape(shape.name)
    )
    .map((shape) => toOption(shape));
};

export const getShapeIdOptions = (
  projectSchema: ProjectSchemaJSON,
  filter: 'models' | 'non-models' | 'any',
  excludeFormlessShapes?: boolean
) => {
  return getShapeOptionsHelper<IdOption>(projectSchema, filter, toIdOption, excludeFormlessShapes);
};

export const getShapeOptions = createSelector(
  [
    getSchema,
    (_: unknown, { format }: ShapeOptionsProps) => format,
    (_: unknown, { filter }: ShapeOptionsProps) => filter
  ],
  (projectSchema, format, filter): Option[] =>
    getShapeOptionsHelper<IdOption | PropertyOption>(
      projectSchema,
      filter,
      format === 'ids' ? toIdOption : toPropertyOption
    )
);

function getPropSchema(schema: PropertySchema, name: string): PropertySchema | undefined {
  schema = schema.items ?? schema;
  return schema.properties ? schema.properties[name] : undefined;
}

function removeNonLocalizedFieldsFromOrder(
  projectSchema: ProjectSchemaJSON,
  form: FormConfig,
  schema: PropertySchema
): FormConfig {
  const order = form.order.filter((name: string): boolean => {
    const propSchema = getPropSchema(schema, name);
    return Boolean(
      propSchema &&
        (propSchema['@l10n'] ??
          (followRef(projectSchema, propSchema).type === 'object' &&
            !propSchema['@tag'] &&
            !propSchema['@resolver'] &&
            !isBuiltinType(projectSchema, propSchema)))
    );
  });

  const properties: Record<string, FormConfig> = {};
  for (const name of order) {
    const propForm = form.properties[name];
    if (propForm?.order) {
      const propSchema = getPropSchema(schema, name);
      if (propSchema) {
        properties[name] = removeNonLocalizedFieldsFromOrder(
          projectSchema,
          propForm,
          followRef(projectSchema, propSchema.items ?? propSchema)
        );
      }
    } else {
      properties[name] = propForm;
    }
  }

  return {
    ...form,
    order,
    properties
  };
}

export function getLocaleFormConfig(
  projectSchema: ProjectSchemaJSON,
  schema: PropertySchema,
  formsConfig: FormsConfig | undefined,
  locale: string | undefined
): FormConfig | undefined {
  if (formsConfig) {
    if (!locale || locale === projectSchema.defaultLocale) {
      return formsConfig.default;
    }

    return removeNonLocalizedFieldsFromOrder(projectSchema, formsConfig.default, schema);
  }
}

export function getLocaleFormConfigs(projectSchema: ProjectSchemaJSON, locale: string): FormMap {
  const result: FormMap = {};
  for (const shape of Object.values(projectSchema.shapes)) {
    const form = projectSchema.forms?.[shape.name];
    if (form) {
      const localizedForm = getLocaleFormConfig(projectSchema, shape.schema, form, locale);
      if (localizedForm) {
        result[shape.name] = {
          default: localizedForm
        };
      }
    }
  }

  return result;
}

export const getFormConfigByLocale = createCachedSelector(
  getSchema,
  getShapeById,
  getLocale,
  (projectSchema, shape, locale): FormConfig | undefined =>
    shape &&
    projectSchema.forms &&
    getLocaleFormConfig(projectSchema, shape.schema, projectSchema.forms[shape.name], locale)
)((_: unknown, props) => `${props.shapeId}:${props.locale}`);

export const getFormConfigsByLocale = createCachedSelector(
  getSchema,
  getLocale,
  (_, { projectId }: { projectId: string }) => projectId,
  getLocaleFormConfigs
)((_: unknown, props) => `${props.projectId}:${props.locale}`);

export const getShapeFormSchemaImmutable = createCachedSelector(getShapeById, getSchema, (shape, projectSchema) => {
  if (!shape) {
    throw new Error('Missing shape');
  }

  return fromJS(shapeToPropertySchema(projectSchema, shape));
})((_: unknown, props) => props.shapeId);

export const getFormConfigByLocaleImmutable = createCachedSelector(getFormConfigByLocale, (config) => fromJS(config))(
  (_: unknown, props) => `${props.shapeId}:${props.locale}`
);

export const getFormConfigsByLocaleImmutable = createCachedSelector(getFormConfigsByLocale, (config) => fromJS(config))(
  (_: unknown, props) => `${props.projectId}:${props.locale}`
);

export const getContentSchema = createSelector(getShapeByName, getSchema, (shape, projectSchema) => {
  if (!shape) {
    throw new Error('Missing shape');
  }

  return shapeToPropertySchema(projectSchema, shape);
});

// TODO This works for the UI, but does not work for validation
const tweak = (projectSchema: ProjectSchemaJSON): ProjectSchemaJSON => {
  return produce<ProjectSchemaJSON, ProjectSchemaJSON>(projectSchema, (draft) => {
    if (
      projectSchema.shapes.Shopify_ProductVariantInput &&
      isObjectSchema(draft.shapes.Shopify_ProductVariantInput.schema)
    ) {
      draft.shapes.Shopify_ProductVariantInput.schema.required = ['productId'];
    }

    if (projectSchema.shapes.Shopify_ProductInput && isObjectSchema(draft.shapes.Shopify_ProductInput.schema)) {
      draft.shapes.Shopify_ProductInput.schema.required = ['title'];
    }

    if (projectSchema.shapes.Shopify_CollectionInput && isObjectSchema(draft.shapes.Shopify_CollectionInput.schema)) {
      draft.shapes.Shopify_CollectionInput.schema.required = ['title'];
    }
  });
};

export const getDataEditorShape = createCachedSelector(
  getInputShapeByName,
  getInputSchema,
  getShapeByName,
  getSchema,
  (inputShape, inputRuntimeSchema, shape, runtimeSchema): [ProjectSchemaJSON, ShapeJSON] => {
    assert(inputShape && shape, 'Missing Shape');

    const serviceInfo = getServiceInfo(runtimeSchema, shape);

    const hasServiceObject = Object.entries(serviceInfo).find(([, serviceInfo]) =>
      serviceInfo.find((item) => item.generators[SERVICE_OBJECT_PATTERN_NAME])
    );

    // TODO remove this once migration has been run
    const interfaceShape = inputRuntimeSchema.shapes[`${inputShape.name}Interface`];
    if (hasServiceObject && interfaceShape) {
      inputShape = interfaceShape;
    } else {
      inputShape = {
        ...inputShape,
        schema: dereferenceObjectSchema(inputRuntimeSchema, inputShape.schema)
      };
    }

    return [hasServiceObject ? tweak(inputRuntimeSchema) : inputRuntimeSchema, inputShape];
  }
)((_, props) => props.shapeName);

export const getDataEditorSchema = createSelector(getDataEditorShape, (dataEditorShape) => {
  return shapeToPropertySchema.apply(null, dataEditorShape);
});

export const getServices = createSelector(getSchema, (schema) => schema.services ?? {});

/**
 *
 */
export const getIntegrationTokens = createSelector(
  getServices,
  (services): Record<string, boolean> =>
    mapValues(
      services,
      (service: ServiceConfigJSON): boolean => service.authenticationType !== 'none' && Boolean(service.authentication)
    )
);

export const getServiceById = createSelector(getServices, getServiceId, (services, serviceId) =>
  serviceId ? services[serviceId] : undefined
);

// Schema History
export const getSchemaHistoryState = (state: SchemaHistorySlice): SchemaHistory => state[SCHEMA_HISTORY_NAME];
export const getSchemaHistoryStatus = createSelector(getSchemaHistoryState, (state) => omit(state, 'versions'));
export const getSchemaHistoryVersionList = createSelector(getSchemaHistoryState, (state) => state.versions);

export const getSchemaTab = createSelector(getState, (state) => state.schemaTab);
export const getSchemaView = createSelector(getState, (state) => state.schemaView);

export const getSchemaFilters = createSelector(getState, getRouter, (schemaState, routerState): SchemaFilters => {
  const routerFilters = JSON.parse(getSingle(routerState.query?.schemaFilters) ?? '{}');
  return {
    ...schemaState.schemaFilters,
    ...routerFilters
  };
});

export const getMonacoCodeEditor = createSelector(getState, (state) => state.monacoCodeEditor);

export const getDeltaDecorations = createSelector(getState, (state) => state.deltaDecorations);

// Indexed Shapes
export const getIndexedShapes = createSelector(getSchema, (schema) => listCachedShapes(schema.shapes));
export const getIndexedShapeNames = createSelector(getIndexedShapes, (shapes) => shapes.map((shape) => shape.name));

export const getSchemaIndexScrollTo = createSelector(getState, (state) => state.schemaIndexScrollTo);

export const getIntrospectDialogServiceId = createSelector(getState, (state) => state.introspectDialogServiceId);

export const getServiceConfigDialogId = createSelector(getState, (state) => state.serviceConfigDialogId);

export const getServiceConfigDialogOpen = createSelector(getState, (state) => state.serviceConfigDialogOpen);
